import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, Trophy, Camera, AlertTriangle, Shield, 
  Users, Clock, ChevronLeft, Zap, CheckCircle, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const MatchRules = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="w-6 h-6 text-[#00F0FF]" />
            <span className="font-heading font-bold text-lg">MATCHSTAKE</span>
          </div>
          <Button onClick={() => navigate(-1)} variant="outline" className="btn-secondary">
            <ChevronLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-[#7000FF]/20 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-[#7000FF]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold">Match Rules</h1>
              <p className="text-white/50">How skill-based competitions work</p>
            </div>
          </div>

          {/* Skill-Based Notice */}
          <div className="p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-8">
            <p className="text-[#00FF94] font-medium">
              MatchStake hosts skill-based competitive gaming matches where outcomes are 
              determined by player skill and in-game performance.
            </p>
          </div>

          <div className="space-y-8">
            {/* How Matches Work */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#CCFF00]" />
                How Competitions Work
              </h2>
              <div className="space-y-4 text-white/70">
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">1. Create or Join a Match</h3>
                  <p>Players create matches by selecting a game, entry fee, and their skill level. Other players can browse open matches and join ones that fit their preferences.</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">2. Confirm Participation</h3>
                  <p>Both players must confirm their participation before the match begins. The match timer only starts after both players click "Accept Match."</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">3. Play Your Game</h3>
                  <p>Players compete in the selected game. Match outcomes are determined entirely by player skill and in-game performance.</p>
                </div>
                <div className="p-4 rounded-lg bg-white/5">
                  <h3 className="font-semibold text-white mb-2">4. Submit Results</h3>
                  <p>After the game, both players submit their results. Screenshot proof may be required to verify the outcome.</p>
                </div>
              </div>
            </section>

            {/* Proof Submission */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-[#00F0FF]" />
                Proof Submission
              </h2>
              <div className="space-y-3 text-white/70">
                <p>When claiming victory, players should submit proof of the match outcome:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Take a clear screenshot of the final game score or result screen</li>
                  <li>Upload the screenshot when submitting your match result</li>
                  <li>Proof helps verify outcomes and resolve disputes quickly</li>
                  <li>Both players can agree to bypass proof submission if mutually agreed</li>
                </ul>
              </div>
            </section>

            {/* Result Scenarios */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#7000FF]" />
                Result Scenarios
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-[#00FF94]/5 border border-[#00FF94]/20">
                  <CheckCircle className="w-6 h-6 text-[#00FF94] mb-2" />
                  <h3 className="font-semibold text-white mb-2">Agreement</h3>
                  <p className="text-white/60 text-sm">When both players agree on the result, the match is completed immediately and the winner receives the reward.</p>
                </div>
                <div className="p-4 rounded-lg bg-[#FF2E2E]/5 border border-[#FF2E2E]/20">
                  <XCircle className="w-6 h-6 text-[#FF2E2E] mb-2" />
                  <h3 className="font-semibold text-white mb-2">Dispute</h3>
                  <p className="text-white/60 text-sm">If players submit conflicting results (both claim victory), the match is flagged as disputed for admin review.</p>
                </div>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FFB800]" />
                Dispute Resolution
              </h2>
              <div className="space-y-3 text-white/70">
                <p>When a match is disputed, our administrative team reviews the case:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Administrators review proof submitted by both players</li>
                  <li>Match details and timestamps are examined</li>
                  <li>A final decision is made based on available evidence</li>
                  <li>The winner is awarded the competition reward, or a draw is declared</li>
                  <li>If no clear winner can be determined, both players may be refunded</li>
                </ul>
              </div>
            </section>

            {/* Match Cancellation */}
            <section>
              <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF2E2E]" />
                Match Cancellation
              </h2>
              <div className="space-y-3 text-white/70">
                <p>Matches may be cancelled and refunded in the following scenarios:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Either player cancels before both have confirmed</li>
                  <li>The match timer expires without results being submitted</li>
                  <li>Technical issues prevent fair competition</li>
                  <li>Administrator determines cancellation is necessary</li>
                </ul>
                <p className="mt-4">When a match is cancelled, entry fees are refunded to both players' wallets.</p>
              </div>
            </section>

            {/* Fair Play */}
            <section className="p-4 rounded-xl bg-[#7000FF]/10 border border-[#7000FF]/20">
              <h2 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#7000FF]" />
                Fair Play Policy
              </h2>
              <ul className="text-white/70 space-y-2 text-sm">
                <li>• All players must compete fairly and honestly</li>
                <li>• Cheating, exploits, or unsportsmanlike conduct may result in account suspension</li>
                <li>• Submitting false proof or results is prohibited</li>
                <li>• Players must use their own accounts and skills</li>
              </ul>
            </section>

            {/* Contact */}
            <section className="text-center pt-6 border-t border-white/10">
              <p className="text-white/50">
                Questions about match rules? Contact our support team.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MatchRules;
